import React from 'react';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { toast } from 'react-toastify';

import { useLancamentoAvulso } from './hooks/useLancamentoAvulso';
import DialogConfirmarLancamento from './DialogConfirmarLancamento';
import DialogLancamentoWrapper from './DialogLancamentoWrapper';
import FormularioLancamentoAvulso from './FormularioLancamentoAvulso';
import TabelaLancamentosAvulsos from './TabelaLancamentosAvulsos';

/**
 * Valor máximo padrão para lançamentos avulsos.
 * @type {number}
 */
const DEFAULT_MAX_VALOR_LANCAMENTO = 10000;

/**
 * Mapa de valores máximos por credor.
 * @type {Record<string, number>}
 */
const MAX_VALOR_POR_CREDOR = {
    '6e39ee2c-55e6-4e3b-bab5-623e377dd56b': 25000,
};

/**
 * Retorna o valor máximo permitido para o credor informado.
 *
 * @param {string|null|undefined} credorId
 * @returns {number}
 */
function getMaxValorLancamento(credorId) {
    if (!credorId) {
        return DEFAULT_MAX_VALOR_LANCAMENTO;
    }

    return MAX_VALOR_POR_CREDOR[credorId] ?? DEFAULT_MAX_VALOR_LANCAMENTO;
}

/**
 * Componente principal da página de lançamento avulso.
 *
 * @param {{ close?: Function, reload?: Function, itemInfo?: Record<string, any> }} props
 * @returns {JSX.Element}
 */
export default function LancamentoAvulso({ close, reload, itemInfo }) {
    const {
        lancamento,
        setLancamento,
        listaLancamentos,
        rowData,
        setRowData,
        showDialog,
        setShowDialog,
        isLoading,
        uploadRef,
        handleSubmit,
        handleAction,
        uploadImagem,
        fecharDialogs,
        abrirBoleto,
        boletoLoadingId
    } = useLancamentoAvulso(itemInfo);

    const valorMaximoPermitido = getMaxValorLancamento(itemInfo?.credor_id);

    return (
        <>
            {showDialog.delete && (
                <ConfirmDialog
                    header="Confirmação"
                    message="Deseja mesmo excluir este lançamento?"
                    visible
                    onHide={() => setShowDialog((prev) => ({ ...prev, delete: false }))}
                    acceptLabel="Excluir"
                    rejectLabel="Cancelar"
                    accept={() => handleAction('delete')}
                />
            )}

            <DialogConfirmarLancamento
                visible={!!showDialog.confirm}
                tipo={showDialog.confirm}
                onHide={() => setShowDialog((prev) => ({ ...prev, confirm: false }))}
                onConfirm={() => handleAction(showDialog.confirm)}
                loading={isLoading}
                lancamento={lancamento}
            />

            <Dialog
                className="md:w-fit w-full"
                header="Adicionar lançamento"
                visible={showDialog.create}
                onHide={() => setShowDialog((prev) => ({ ...prev, create: false }))}
            >
                <FormularioLancamentoAvulso
                    lancamento={lancamento}
                    setLancamento={setLancamento}
                    onSubmit={handleSubmit}
                    isLoading={isLoading}
                    modo="create"
                    uploadImagem={uploadImagem}
                    valorMaximoPermitido={valorMaximoPermitido}
                />
            </Dialog>

            <Dialog
                header="Atualizar lançamento"
                visible={showDialog.update}
                onHide={() => setShowDialog((prev) => ({ ...prev, update: false }))}
                onShow={() => {
                    if (!rowData) return;

                    setLancamento({
                        descricao: rowData.lancamento_descricao,
                        valor: Number(rowData.lancamento_valor),
                        dataRef:
                            rowData.ano_referencia && rowData.mes_referencia
                                ? new Date(`${rowData.ano_referencia}-${String(rowData.mes_referencia).padStart(2, '0')}-01T00:00:00`)
                                : null,
                        vencimento: rowData.lancamento_vencimento_date
                            ? rowData.lancamento_vencimento_date
                            : rowData.lancamento_vencimento
                                ? new Date(`${rowData.lancamento_vencimento}T00:00:00`)
                                : null,
                        imagem: {},
                    });
                }}
                className="lg:w-1/3 md:3/4"
            >
                <FormularioLancamentoAvulso
                    lancamento={lancamento}
                    setLancamento={setLancamento}
                    onSubmit={handleSubmit}
                    isLoading={isLoading}
                    modo="edit"
                    uploadImagem={uploadImagem}
                    uploadRef={uploadRef}
                    valorMaximoPermitido={valorMaximoPermitido}
                />

                <div className="w-full flex justify-center gap-2 mt-3">
                    <button
                        className="p-button p-component p-button-danger"
                        type="button"
                        onClick={() => setShowDialog((prev) => ({ ...prev, delete: true }))}
                    >
                        Remover
                    </button>
                </div>
            </Dialog>

            <DialogLancamentoWrapper
                visible
                onHide={() => {
                    fecharDialogs();
                    close?.();
                    reload?.();
                }}
            >
                <TabelaLancamentosAvulsos
                    lista={listaLancamentos}
                    infoUsuario={itemInfo}
                    onSelecionar={(selected) => {
                        if (selected?.lancamento_situacao !== 'cadastrado') {
                            return toast.error('Apenas lançamentos com situação "Cadastrado" podem ser editados.');
                        }
                        setRowData(selected);
                        setShowDialog((prev) => ({ ...prev, update: true }));
                    }}
                    boletoLoadingId={boletoLoadingId}
                    onAbrirBoleto={abrirBoleto}
                    onNovo={() => {
                        setLancamento({
                            descricao: '',
                            valor: '',
                            dataRef: '',
                            vencimento: '',
                            imagem: {}
                        });
                        setShowDialog((prev) => ({ ...prev, create: true }));
                    }}
                />
            </DialogLancamentoWrapper>
        </>
    );
}